import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { EntityList, PageHead } from '../../components/EntityList';
import creditService from '../../services/credit.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import CreditDateEditor from './CreditDateEditor';

/**
 * Page shell for CreditDateEditor — this used to be an xl modal opened from
 * CreditDetails' header. Only the credit's customId is fetched here (for the
 * heading); the editor still loads the full credit itself.
 */
function EditCreditStartDate() {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [customId, setCustomId] = useState('');

	const backTo = `/AdminScreen/credits/credit/${id}/info`;

	// Stable identity — CreditDateEditor's fetch effect keys off this prop, so a
	// fresh literal on every render of this page would refetch the credit.
	const initialData = useMemo(() => ({ _id: id }), [id]);

	useEffect(() => {
		let isCurrent = true;
		creditService
			.getDetails(id)
			.then(({ data }) => {
				if (isCurrent) setCustomId(data?.customId || '');
			})
			.catch((ex) => toast.error(getError(ex)));
		return () => {
			isCurrent = false;
		};
	}, [id]);

	const title = customId ? `${t('action.editStartDate')} — ${customId}` : t('action.editStartDate');

	return (
		<EntityList>
			<Helmet>
				<title>{t('action.editStartDate')}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.credit.plural'), to: '/AdminScreen/credits' },
					{ label: customId || t('entity.credit'), to: backTo },
					{ label: t('action.editStartDate') },
				]}
				title={title}
				subtitle='Ajustá la fecha de inicio y el primer vencimiento, y elegí qué hacer con las cuotas existentes.'
			/>
			<CreditDateEditor
				initialData={initialData}
				onSaved={() => navigate(backTo)}
				cancelHandler={() => navigate(backTo)}
			/>
		</EntityList>
	);
}

export default EditCreditStartDate;
